import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Checkbox, Icon, Input, Label, cn } from "@nexus/ui";
import { isValidSignerName } from "../lib/signer-name";

interface TermsSignaturePanelProps {
  vendorName: string;
  termsMarkdown: string | null;
  termsVersion: number;
  signerName: string;
  termsScrolled: boolean;
  termsAccepted: boolean;
  onScrolled: () => void;
  onAcceptedChange: (accepted: boolean) => void;
  onSignerNameChange: (name: string) => void;
  /** Disambiguates element ids when two panels mount in the same document. */
  idPrefix: string;
}

/** Treat the agreement as fully read once the scroll position reaches this gap. */
const SCROLL_BOTTOM_THRESHOLD_PX = 16;
/** scrollHeight can exceed clientHeight by a hairline even with no real overflow. */
const NO_OVERFLOW_TOLERANCE_PX = 4;
/**
 * UX cap for a legal name — well under the API `signerName` limit (255) so the
 * field can't accept an unrealistic, layout-breaking string.
 */
const SIGNER_NAME_MAX_LENGTH = 80;

function formatSignedDate(): string {
  return new Date().toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function TermsSignaturePanel({
  vendorName,
  termsMarkdown,
  termsVersion,
  signerName,
  termsScrolled,
  termsAccepted,
  onScrolled,
  onAcceptedChange,
  onSignerNameChange,
  idPrefix,
}: TermsSignaturePanelProps) {
  const { t } = useTranslation();
  const termsRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(termsScrolled ? 100 : 0);
  const [nameTouched, setNameTouched] = useState(false);

  const hasTerms = !!termsMarkdown?.trim();
  const fullyRead = !hasTerms || termsScrolled;
  // Once the reading gate is satisfied the bar is always full, even if the
  // scroll threshold fired a few pixels before the literal bottom.
  const displayProgress = termsScrolled ? 100 : progress;
  const trimmedName = signerName.trim();
  const hasSignature = trimmedName.length > 0;
  const nameValid = isValidSignerName(trimmedName);
  const signedDate = formatSignedDate();
  const nameMissing = nameTouched && !hasSignature;
  const nameInvalid = nameTouched && hasSignature && !nameValid;
  const nameError = nameMissing || nameInvalid;
  const nameErrorId = `${idPrefix}-signer-name-error`;

  // Auto-clear the reading gate when there is nothing to scroll: no terms at
  // all, or terms short enough to fit without overflow.
  useEffect(() => {
    if (!hasTerms) {
      setProgress(100);
      if (!termsScrolled) onScrolled();
      return;
    }
    const el = termsRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + NO_OVERFLOW_TOLERANCE_PX) {
      setProgress(100);
      if (!termsScrolled) onScrolled();
    }
  }, [hasTerms, termsScrolled, onScrolled]);

  function handleScroll(event: React.UIEvent<HTMLDivElement>) {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const scrollable = scrollHeight - clientHeight;
    const percent =
      scrollable <= 0
        ? 100
        : Math.min(100, Math.round((scrollTop / scrollable) * 100));
    setProgress(percent);

    if (
      !termsScrolled &&
      scrollTop + clientHeight >= scrollHeight - SCROLL_BOTTOM_THRESHOLD_PX
    ) {
      onScrolled();
    }
  }

  return (
    <div className="grid gap-5 lg:h-full lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
      <div className="overflow-hidden rounded-lg border border-border bg-card lg:flex lg:h-full lg:min-h-0 lg:flex-col">
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10">
              <Icon name="file-text" size="sm" color="primary" />
            </span>
            <span className="truncate text-[13px] font-medium text-foreground">
              {t("marketplace.wizard.terms.agreementTitle")}
            </span>
            <Badge variant="outline" className="shrink-0">
              {t("marketplace.wizard.terms.version", { version: termsVersion })}
            </Badge>
          </div>
          {fullyRead ? (
            <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-success animate-in fade-in-0 zoom-in-95 duration-300 motion-reduce:animate-none">
              <Icon name="check-circle" size="sm" color="inherit" />
              {t("marketplace.wizard.terms.read")}
            </span>
          ) : (
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {t("marketplace.wizard.terms.progress", { percent: displayProgress })}
            </span>
          )}
        </div>

        <div
          className="h-0.5 w-full bg-muted"
          role="progressbar"
          aria-valuenow={displayProgress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t("marketplace.wizard.terms.progressLabel")}
        >
          <div
            className="h-full bg-primary transition-[width] duration-300 ease-out motion-reduce:transition-none"
            style={{ width: `${displayProgress}%` }}
          />
        </div>

        {hasTerms ? (
          <div className="relative lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
            <div
              ref={termsRef}
              onScroll={handleScroll}
              className="max-h-[320px] overflow-y-auto px-4 py-4 lg:max-h-none lg:min-h-0 lg:flex-1"
              tabIndex={0}
              aria-label={t("marketplace.wizard.terms.document")}
            >
              <pre className="whitespace-pre-wrap break-words font-sans text-xs leading-relaxed text-foreground">
                {termsMarkdown}
              </pre>
            </div>
            {!fullyRead && (
              <div
                className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-card to-transparent"
                aria-hidden
              />
            )}
          </div>
        ) : (
          <p className="px-4 py-4 text-sm text-muted-foreground">
            {t("marketplace.wizard.terms.noTerms", { vendorName })}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-4 rounded-lg border border-border bg-card p-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] font-medium text-foreground">
            {t("marketplace.wizard.terms.signSectionTitle")}
          </span>
          {nameValid && fullyRead && termsAccepted && (
            <span className="text-xs text-muted-foreground animate-in fade-in-0 duration-300 motion-reduce:animate-none">
              {t("marketplace.wizard.terms.signedOn", { date: signedDate })}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <div
            className={cn(
              "flex items-start gap-3 transition-opacity",
              !fullyRead && "opacity-60",
            )}
          >
            <Checkbox
              id={`${idPrefix}-terms-accept`}
              checked={termsAccepted}
              disabled={!fullyRead}
              onCheckedChange={(checked) => onAcceptedChange(checked === true)}
              className="mt-0.5"
            />
            <Label
              htmlFor={`${idPrefix}-terms-accept`}
              className={cn(
                "text-xs leading-relaxed",
                fullyRead ? "cursor-pointer text-foreground" : "text-muted-foreground",
              )}
            >
              {t("marketplace.wizard.terms.accept")}
            </Label>
          </div>
          {hasTerms && !fullyRead && (
            <p className="pl-7 text-xs text-muted-foreground">
              {t("marketplace.wizard.terms.scrollHint")}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-signer-name`} className="text-xs font-medium">
            {t("marketplace.wizard.terms.signerLabel")}
            <span className="ml-0.5 text-destructive">*</span>
          </Label>
          <Input
            id={`${idPrefix}-signer-name`}
            value={signerName}
            onChange={(event) => onSignerNameChange(event.target.value)}
            onBlur={() => setNameTouched(true)}
            placeholder={t("marketplace.wizard.terms.signerPlaceholder")}
            autoComplete="name"
            maxLength={SIGNER_NAME_MAX_LENGTH}
            aria-invalid={nameError || undefined}
            aria-describedby={nameError ? nameErrorId : undefined}
          />
          {nameError && (
            <p
              id={nameErrorId}
              role="alert"
              className="flex items-center gap-1 text-xs text-destructive animate-in fade-in-0 slide-in-from-top-1 duration-200 motion-reduce:animate-none"
            >
              <Icon name="alert-circle" size="xs" color="destructive" />
              {t(
                nameInvalid
                  ? "marketplace.wizard.terms.signerInvalid"
                  : "marketplace.wizard.terms.signerRequired",
              )}
            </p>
          )}
        </div>

        <div className="overflow-hidden rounded-md border border-dashed border-border bg-muted/30 px-4 pb-3 pt-4">
          <div className="flex min-h-[44px] items-end">
            {nameValid ? (
              <span
                key={trimmedName}
                className="min-w-0 flex-1 truncate font-signature text-2xl leading-none text-foreground animate-in fade-in-0 slide-in-from-bottom-1 duration-300 motion-reduce:animate-none"
              >
                {signerName}
              </span>
            ) : (
              <span className="min-w-0 flex-1 truncate font-signature text-2xl leading-none text-muted-foreground/50">
                {t("marketplace.wizard.terms.signaturePlaceholder")}
              </span>
            )}
          </div>
          <div className="mt-2 flex items-end justify-between gap-4 border-t border-border pt-2">
            <div className="flex min-w-0 flex-col">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {t("marketplace.wizard.terms.signatureLabel")}
              </span>
              <span className="truncate text-xs text-foreground">
                {nameValid ? signerName : "—"}
              </span>
            </div>
            <div className="flex shrink-0 flex-col items-end">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {t("marketplace.wizard.terms.dateLabel")}
              </span>
              <span className="text-xs tabular-nums text-foreground">{signedDate}</span>
            </div>
          </div>
        </div>

        <p className="text-[11px] leading-relaxed text-muted-foreground">
          {t("marketplace.wizard.terms.legalNote")}
        </p>
      </div>
    </div>
  );
}
